'use client';

import * as React from 'react';
import Link from 'next/link';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '@/components/ui/accordion';

export function FAQ() {
  return (
    <section className="py-16 bg-gray-50" id="vanliga-fragor">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Vanliga frågor
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Här hittar du svar på de vanligaste frågorna om våra tjänster inom montage och demontage av kranar
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1" className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
              <AccordionTrigger className="p-6 hover:bg-gray-50 text-left text-xl font-semibold text-gray-900">
                Hur ofta behöver en kran genomgå service?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 text-gray-700 text-lg">
                <p>
                  Enligt <a href="https://www.av.se" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Arbetsmiljöverkets</a> regler ska kranar och lyftanordningar besiktigas årligen. 
                  Utöver den årliga besiktningen rekommenderar vi även kvartalsvis service för att säkerställa optimal funktion och säkerhet. 
                  Detta kan variera beroende på användningsfrekvens och driftsmiljö. Läs mer om våra <Link href="/krantjanster/service-underhall" className="text-blue-600 hover:underline">underhållstjänster</Link> eller 
                  kontakta oss för en skräddarsydd serviceplan för dina specifika behov.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
              <AccordionTrigger className="p-6 hover:bg-gray-50 text-left text-xl font-semibold text-gray-900">
                Vad kostar det att installera en ny kran?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 text-gray-700 text-lg">
                <p>
                  Kostnaden för kraninstallation varierar beroende på typen av kran, kapacitet, spännvidd, och komplexiteten i installationen. 
                  Vi erbjuder kostnadsfria konsultationer och offerter för att ge dig en exakt prisuppgift baserad på dina specifika krav. 
                  Se vårt utbud av <Link href="/kranar-lyftanordningar" className="text-blue-600 hover:underline">kranar och lyftanordningar</Link> eller 
                  <Link href="/kontakta-oss" className="text-blue-600 hover:underline"> kontakta oss</Link> för att boka en konsultation och få en detaljerad offert för 
                  <Link href="/krantjanster/montage-demontage" className="text-blue-600 hover:underline"> kraninstallation</Link>.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
              <AccordionTrigger className="p-6 hover:bg-gray-50 text-left text-xl font-semibold text-gray-900">
                Kan ni uppgradera vårt befintliga radiosystem?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 text-gray-700 text-lg">
                <p>
                  Ja, vi kan uppgradera och modernisera befintliga radiosystem för kranar. Moderna radiosystem erbjuder bättre säkerhet, precision och funktionalitet. 
                  Vi analyserar era nuvarande system och rekommenderar lämpliga uppgraderingar baserat på era behov. Se mer information om våra 
                  <Link href="/krantjanster/radiosystem" className="text-blue-600 hover:underline"> radiosystem och kranstyrningslösningar</Link> och hur de kan förbättra effektiviteten för er verksamhet. 
                  Vi arbetar med ledande tillverkare för att leverera de mest pålitliga och framtidssäkrade lösningarna.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
              <AccordionTrigger className="p-6 hover:bg-gray-50 text-left text-xl font-semibold text-gray-900">
                Hur snabbt kan ni hjälpa vid en kranolycka eller driftstopp?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 text-gray-700 text-lg">
                <p>
                  Vi förstår att driftstopp kan orsaka allvarliga produktionsproblem. Därför erbjuder vi 
                  <Link href="/krantjanster/reparation" className="text-blue-600 hover:underline"> akut service</Link> med snabb responstid, oftast inom 24 timmar i hela Östergötland och omgivande regioner. 
                  För nödsituationer har vi ett journummer som är tillgängligt dygnet runt. Vår erfarna personal är utbildad för att snabbt diagnostisera och åtgärda problem för att minimera driftstopp. 
                  Läs mer om våra <Link href="/krantjanster/reparation" className="text-blue-600 hover:underline">reparationstjänster</Link>.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
              <AccordionTrigger className="p-6 hover:bg-gray-50 text-left text-xl font-semibold text-gray-900">
                Vilka garantier erbjuder ni på era produkter och tjänster?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 text-gray-700 text-lg">
                <p>
                  Vi erbjuder omfattande garantier på både våra <Link href="/kranar-lyftanordningar" className="text-blue-600 hover:underline">produkter</Link> och 
                  <Link href="/krantjanster" className="text-blue-600 hover:underline"> tjänster</Link>. 
                  Nya kranar och lyftanordningar levereras med en standardgaranti på 2 år, och våra servicetjänster omfattas av en 6-månaders arbetsgaranti. 
                  Vi står bakom kvaliteten på vårt arbete och använder endast certifierade reservdelar. För mer detaljerad information om våra garantivillkor, 
                  vänligen <Link href="/kontakta-oss" className="text-blue-600 hover:underline">kontakta vår kundtjänst</Link>.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-4">
              <AccordionTrigger className="p-6 hover:bg-gray-50 text-left text-xl font-semibold text-gray-900">
                Är era tekniker certifierade för kranservice?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 text-gray-700 text-lg">
                <p>
                  Ja, alla våra tekniker är certifierade och kontinuerligt utbildade enligt de senaste branschstandarderna. 
                  De har omfattande erfarenhet och expertis inom kraninstallation, service och reparation. 
                  Vårt team håller sig uppdaterade med nya tekniker och säkerhetsstandarder genom regelbunden utbildning. 
                  Vi följer strikt <a href="https://www.av.se" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Arbetsmiljöverkets</a> riktlinjer 
                  och internationella standarder för att garantera högsta kvalitet och säkerhet i allt vårt arbete. 
                  Läs mer om <Link href="/om-axfalk" className="text-blue-600 hover:underline">vårt företag och våra värderingar</Link>.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <AccordionTrigger className="p-6 hover:bg-gray-50 text-left text-xl font-semibold text-gray-900">
                Vilka områden täcker ni med era tjänster?
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6 text-gray-700 text-lg">
                <p>
                  Vi är baserade i Linköping och täcker hela Östergötland som vårt primära serviceområde. 
                  Därutöver erbjuder vi våra tjänster i större delen av Sverige för större projekt och installationer. 
                  Vårt centrala läge i Linköping gör det möjligt för oss att snabbt nå kunder i hela regionen, 
                  inklusive Norrköping, Motala, Mjölby och omkringliggande kommuner. 
                  För projekt utanför Östergötland planerar vi resor och logistik för att säkerställa effektiv service. 
                  <Link href="/kontakta-oss" className="text-blue-600 hover:underline"> Kontakta oss</Link> för att diskutera ditt specifika projekt och våra möjligheter att hjälpa er.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
        
        <div className="text-center mt-8">
          <p className="text-lg text-gray-600">
            Har du fler frågor? <Link href="/kontakta-oss" className="text-blue-600 hover:underline font-medium">Kontakta oss</Link> för mer information.
          </p>
        </div>
      </div>

      {/* FAQ Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Hur ofta behöver en kran genomgå service?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Enligt Arbetsmiljöverkets regler ska kranar och lyftanordningar besiktigas årligen. Utöver den årliga besiktningen rekommenderar vi även kvartalsvis service för att säkerställa optimal funktion och säkerhet. Detta kan variera beroende på användningsfrekvens och driftsmiljö. Läs mer om våra underhållstjänster eller kontakta oss för en skräddarsydd serviceplan för dina specifika behov."
                }
              },
              {
                "@type": "Question",
                "name": "Vad kostar det att installera en ny kran?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Kostnaden för kraninstallation varierar beroende på typen av kran, kapacitet, spännvidd, och komplexiteten i installationen. Vi erbjuder kostnadsfria konsultationer och offerter för att ge dig en exakt prisuppgift baserad på dina specifika krav. Se vårt utbud av kranar och lyftanordningar eller kontakta oss för att boka en konsultation och få en detaljerad offert för kraninstallation."
                }
              },
              {
                "@type": "Question",
                "name": "Kan ni uppgradera vårt befintliga radiosystem?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, vi kan uppgradera och modernisera befintliga radiosystem för kranar. Moderna radiosystem erbjuder bättre säkerhet, precision och funktionalitet. Vi analyserar era nuvarande system och rekommenderar lämpliga uppgraderingar baserat på era behov. Se mer information om våra radiosystem och kranstyrningslösningar och hur de kan förbättra effektiviteten för er verksamhet. Vi arbetar med ledande tillverkare för att leverera de mest pålitliga och framtidssäkrade lösningarna."
                }
              },
              {
                "@type": "Question",
                "name": "Hur snabbt kan ni hjälpa vid en kranolycka eller driftstopp?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Vi förstår att driftstopp kan orsaka allvarliga produktionsproblem. Därför erbjuder vi akut service med snabb responstid, oftast inom 24 timmar i hela Östergötland och omgivande regioner. För nödsituationer har vi ett journummer som är tillgängligt dygnet runt. Vår erfarna personal är utbildad för att snabbt diagnostisera och åtgärda problem för att minimera driftstopp. Läs mer om våra reparationstjänster."
                }
              },
              {
                "@type": "Question",
                "name": "Vilka garantier erbjuder ni på era produkter och tjänster?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Vi erbjuder omfattande garantier på både våra produkter och tjänster. Nya kranar och lyftanordningar levereras med en standardgaranti på 2 år, och våra servicetjänster omfattas av en 6-månaders arbetsgaranti. Vi står bakom kvaliteten på vårt arbete och använder endast certifierade reservdelar. För mer detaljerad information om våra garantivillkor, vänligen kontakta vår kundtjänst."
                }
              },
              {
                "@type": "Question",
                "name": "Är era tekniker certifierade för kranservice?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, alla våra tekniker är certifierade och kontinuerligt utbildade enligt de senaste branschstandarderna. De har omfattande erfarenhet och expertis inom kraninstallation, service och reparation. Vårt team håller sig uppdaterade med nya tekniker och säkerhetsstandarder genom regelbunden utbildning. Vi följer strikt Arbetsmiljöverkets riktlinjer och internationella standarder för att garantera högsta kvalitet och säkerhet i allt vårt arbete. Läs mer om vårt företag och våra värderingar."
                }
              },
              {
                "@type": "Question",
                "name": "Vilka områden täcker ni med era tjänster?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Vi är baserade i Linköping och täcker hela Östergötland som vårt primära serviceområde. Därutöver erbjuder vi våra tjänster i större delen av Sverige för större projekt och installationer. Vårt centrala läge i Linköping gör det möjligt för oss att snabbt nå kunder i hela regionen, inklusive Norrköping, Motala, Mjölby och omkringliggande kommuner. För projekt utanför Östergötland planerar vi resor och logistik för att säkerställa effektiv service. Kontakta oss för att diskutera ditt specifika projekt och våra möjligheter att hjälpa er."
                }
              }
            ]
          })
        }}
      />
    </section>
  );
}
